import { useState } from 'react'
import ThemeMaker from '../../components/thememaker'
import { toast } from 'react-toastify';
import axios from 'axios';
import { config } from '../../utils/config';
import "../../index.css";
import styles from './UpdatePassword.module.css';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

const MIN_PASSWORD_LENGTH = 8;
const MAX_PASSWORD_LENGTH = 64;
const PASSWORD_FORMAT_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9\s])[\x21-\x7E]+$/;

type PasswordData = {
    newPassword: string;
    oldPassword: string;
    confirmPassword: string;
};

type PasswordErrors = {
    newPassword: string;
    confirmPassword: string;
};

const EMPTY_PASSWORD_ERRORS: PasswordErrors = {
    newPassword: "",
    confirmPassword: "",
};

function validatePasswordData(passwordData: PasswordData): PasswordErrors {
    const errors = { ...EMPTY_PASSWORD_ERRORS };
    const { newPassword, confirmPassword } = passwordData;

    if (!newPassword) {
        errors.newPassword = "New password is required.";
    } else if (newPassword.length < MIN_PASSWORD_LENGTH || newPassword.length > MAX_PASSWORD_LENGTH) {
        errors.newPassword = `Password must be ${MIN_PASSWORD_LENGTH}-${MAX_PASSWORD_LENGTH} characters long.`;
    } else if (/\s/.test(newPassword)) {
        errors.newPassword = "Password cannot contain spaces.";
    } else if (!PASSWORD_FORMAT_REGEX.test(newPassword)) {
        errors.newPassword = "Password must include uppercase, lowercase, number, and special character.";
    }

    if (!confirmPassword) {
        errors.confirmPassword = "Confirm password is required.";
    } else if (confirmPassword !== newPassword) {
        errors.confirmPassword = "Passwords do not match.";
    }

    return errors;
}

export default function UpdatePassword() {
    const { apiEndpoint } = config;
    const rawData: PasswordData = {
        newPassword: "",
        oldPassword: "",
        confirmPassword: ""
    }
    const [passwordData, setpasswordData] = useState(rawData);
    const [errors, setErrors] = useState<PasswordErrors>(EMPTY_PASSWORD_ERRORS);
    const [loading, setLoading] = useState(false);

    function onChangeHandler(e: React.ChangeEvent<HTMLInputElement>) {
        const nextPasswordData = {
            ...passwordData,
            [e.target.name]: e.target.value,
        };
        setpasswordData(nextPasswordData);
        setErrors(validatePasswordData(nextPasswordData));
    }

    async function PasswordHandler(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();
        const validationErrors = validatePasswordData(passwordData);
        setErrors(validationErrors);

        if (validationErrors.newPassword || validationErrors.confirmPassword) {
            toast.error("Please correct the password fields.");
            return;
        }

        setLoading(true);
        try {
            const response = await axios({
                url: `${apiEndpoint}/updatepassword`,
                method: "POST",
                headers: {
                    "x-custom-authorization": `Bearer ${sessionStorage.getItem('us_st_d')}`
                },
                data: { password: passwordData.confirmPassword }
            });

            if (response.status >= 200 && response.status < 300) {
                toast.success("Password Updated");
                setpasswordData(rawData);
                setErrors(EMPTY_PASSWORD_ERRORS);
            } else {
                toast.error("Something Went Wrong");
            }
        } catch (error) {
            toast.error("Something Went Wrong");
        } finally {
            setLoading(false);
        }
    }

    // console.log(passwordData)
    return (
        <ThemeMaker>
            <div className={styles.wrap}>
                <div className={styles.card}>
                    <div className={styles.cardHeader}>
                        <div className={styles.cardTitle}>
                            <LockOutlinedIcon style={{ fontSize: 20 }} />
                            Update Password
                        </div>
                        <div className={styles.cardSub}>Keep your account secure with a strong password</div>
                    </div>

                    <form className={styles.cardBody} onSubmit={PasswordHandler}>
                        <div className={styles.formGroup}>
                            <label className={styles.label}>New Password</label>
                            <input
                                type="password"
                                className={styles.input}
                                name="newPassword"
                                placeholder="Enter New Password"
                                value={passwordData.newPassword}
                                onChange={onChangeHandler}
                                aria-invalid={Boolean(errors.newPassword)}
                            />
                            {errors.newPassword && <span className={styles.errorText}>{errors.newPassword}</span>}
                        </div>

                        <div className={styles.formGroup}>
                            <label className={styles.label}>Confirm New Password</label>
                            <input
                                type="password"
                                className={styles.input}
                                name="confirmPassword"
                                placeholder="Confirm Password"
                                value={passwordData.confirmPassword}
                                onChange={onChangeHandler}
                                aria-invalid={Boolean(errors.confirmPassword)}
                            />
                            {errors.confirmPassword && <span className={styles.errorText}>{errors.confirmPassword}</span>}
                        </div>

                        <button className={styles.submitBtn} type="submit" disabled={loading}>
                            {loading ? "Updating..." : "Update"}
                        </button>
                    </form>
                </div>
            </div>

        </ThemeMaker >
    )
}
